// src/utils/dateParser.js
// 智能日期解析器 - 独立文件

export class SmartDateParser {
  constructor() {
    // 天数关键词匹配 - 支持中英文
    this.dayPatterns = [
      // 基础天数匹配
      /(\d+)\s*(?:天|days?|日|nights?)/gi,
      /(?:for|持续|安排)\s*(\d+)\s*(?:天|days?|日)/gi,
      /(\d+)\s*(?:天|日)(?:的|行程|旅行|之旅)/gi,
      // 天夜组合
      /(\d+)\s*天\s*(\d+)\s*(?:夜|晚)/gi,
      /(\d+)\s*(?:天|日)\s*(\d+)\s*(?:夜|晚|nights?)/gi,
      // 周末短途
      /(?:周末|weekend)\s*(?:游|trip|getaway)/gi,
      // 一周游
      /(?:一周|1周|七天|7天|week-long|weekly)/gi
    ];

    // 高级日期范围匹配 - 基于 Chrono.js 的模式
    this.dateRangePatterns = [
      // 中文日期格式
      /(\d{1,2})月(\d{1,2})日?(?:到|至|-)(\d{1,2})日?/gi,
      /(\d{4})年(\d{1,2})月(\d{1,2})日?(?:到|至|-)(\d{1,2})月(\d{1,2})日?/gi,
      // ISO格式
      /(\d{4})-(\d{1,2})-(\d{1,2})\s*(?:to|至|到)\s*(\d{4})-(\d{1,2})-(\d{1,2})/gi,
      // 英文月份格式
      /(january|february|march|april|may|june|july|august|september|october|november|december)\s*(\d{1,2})(?:st|nd|rd|th)?\s*(?:to|至|到|-)\s*(\d{1,2})(?:st|nd|rd|th)?/gi,
      // 简化格式
      /(\d{1,2})\/(\d{1,2})\s*(?:to|至|到|-)\s*(\d{1,2})\/(\d{1,2})/gi
    ];

    // 相对日期匹配 - 基于 NLP 技术
    this.relativeDatePatterns = [
      /(?:下周|next week)/gi,
      /(?:下月|next month)/gi,
      /(?:这周末|this weekend)/gi,
      /(?:明天开始|starting tomorrow)/gi,
      /(?:后天|day after tomorrow)/gi
    ];

    this.monthMap = {
      'january': 1, 'jan': 1, 'february': 2, 'feb': 2, 'march': 3, 'mar': 3,
      'april': 4, 'apr': 4, 'may': 5, 'june': 6, 'jun': 6,
      'july': 7, 'jul': 7, 'august': 8, 'aug': 8, 'september': 9, 'sep': 9,
      'october': 10, 'oct': 10, 'november': 11, 'nov': 11, 'december': 12, 'dec': 12
    };

    // 智能意图识别
    this.intentKeywords = {
      planning: ['计划', '规划', '安排', 'plan', 'arrange', 'organize'],
      budget: ['预算', '费用', '价格', 'budget', 'cost', 'price', 'cheap', 'expensive'],
      activity: ['活动', '景点', '玩', 'activities', 'attractions', 'things to do'],
      food: ['美食', '餐厅', '吃', 'food', 'restaurant', 'dining', 'cuisine']
    };
  }

  parseUserInput(input) {
    const result = {
      days: null,
      startDate: null,
      endDate: null,
      confidence: 0,
      extractedInfo: {},
      intent: null,
      preferences: {}
    };

    const lowerInput = input.toLowerCase();
    console.log('🔍 Parsing input:', input);
    
    // 1. 意图识别 - 基于 NLP 技术
    result.intent = this.detectIntent(lowerInput);
    if (result.intent) {
      result.confidence += 0.2;
    }

    // 2. 高级天数提取 - 支持更多格式
    const dayInfo = this.extractDaysAdvanced(input);
    if (dayInfo.days) {
      result.days = dayInfo.days;
      result.confidence += 0.4;
      result.extractedInfo.dayExtraction = dayInfo;
    }

    // 3. 智能日期范围提取
    const dateInfo = this.extractDateRangeAdvanced(input);
    if (dateInfo.startDate && dateInfo.endDate) {
      result.startDate = dateInfo.startDate;
      result.endDate = dateInfo.endDate;
      result.confidence += 0.5;
      result.extractedInfo.dateExtraction = dateInfo;
      
      // 如果有日期范围但没有天数，智能计算天数
      if (!result.days) {
        const diffTime = new Date(result.endDate) - new Date(result.startDate);
        result.days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }
    }

    // 4. 相对日期处理
    const relativeInfo = this.extractRelativeDates(input);
    if (relativeInfo.startDate && !result.startDate) {
      result.startDate = relativeInfo.startDate;
      result.endDate = relativeInfo.endDate;
      result.confidence += 0.3;
    }

    // 5. 智能默认日期生成
    if (result.days && !result.startDate) {
      const defaultDates = this.generateSmartDefaultDates(result.days, input);
      result.startDate = defaultDates.startDate;
      result.endDate = defaultDates.endDate;
      result.confidence += 0.2;
    }

    // 6. 提取旅行偏好
    const preferences = this.extractTravelPreferences(lowerInput);
    if (Object.keys(preferences).length > 0) {
      result.preferences = preferences;
      result.confidence += 0.1;
    }

    console.log('📊 Parse result:', result);
    return result;
  }

  // 🆕 高级意图检测
  detectIntent(input) {
    const intents = [];
    
    for (const [intent, keywords] of Object.entries(this.intentKeywords)) {
      const matches = keywords.filter(keyword => input.includes(keyword));
      if (matches.length > 0) {
        intents.push({ intent, score: matches.length });
      }
    }

    if (intents.length === 0) return null;
    
    // 返回得分最高的意图
    intents.sort((a, b) => b.score - a.score);
    return intents[0].intent;
  }

  // 🆕 高级天数提取
  extractDaysAdvanced(input) {
    // 1. 标准天数匹配
    for (const pattern of this.dayPatterns) {
      const match = pattern.exec(input);
      if (match) {
        let days = parseInt(match[1]);
        
        // 天夜组合处理
        if (match[2]) {
          const nights = parseInt(match[2]);
          days = Math.max(days, nights + 1); // 通常天数 = 夜数 + 1
        }
        
        if (days > 0 && days <= 30) {
          return {
            days,
            matchText: match[0],
            pattern: pattern.source,
            type: 'explicit'
          };
        }
      }
    }

    // 2. 特殊词汇匹配
    const specialCases = {
      '周末': 2,
      'weekend': 2,
      '一周': 7,
      'week': 7,
      '短途': 3,
      'short trip': 3,
      '长假': 10,
      'long vacation': 10
    };

    for (const [keyword, days] of Object.entries(specialCases)) {
      if (input.toLowerCase().includes(keyword)) {
        return {
          days,
          matchText: keyword,
          type: 'special_case'
        };
      }
    }

    return { days: null };
  }

  // 🆕 高级日期范围提取
  extractDateRangeAdvanced(input) {
    // 1. 中文日期格式增强
    const chineseDateMatch = input.match(/(\d{4})?年?(\d{1,2})月(\d{1,2})日?(?:到|至|-)(\d{1,2})月?(\d{1,2})日?/);
    if (chineseDateMatch) {
      const year = chineseDateMatch[1] ? parseInt(chineseDateMatch[1]) : new Date().getFullYear();
      const startMonth = parseInt(chineseDateMatch[2]);
      const startDay = parseInt(chineseDateMatch[3]);
      const endMonth = chineseDateMatch[4] ? parseInt(chineseDateMatch[4]) : startMonth;
      const endDay = parseInt(chineseDateMatch[5]);
      
      return {
        startDate: `${year}-${startMonth.toString().padStart(2, '0')}-${startDay.toString().padStart(2, '0')}`,
        endDate: `${year}-${endMonth.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')}`,
        format: 'chinese_enhanced'
      };
    }

    // 2. 原有格式保持不变...
    const simpleChineseMatch = input.match(/(\d{1,2})月(\d{1,2})日?(?:到|至|-)(\d{1,2})日?/);
    if (simpleChineseMatch) {
      const currentYear = new Date().getFullYear();
      const month = parseInt(simpleChineseMatch[1]);
      const startDay = parseInt(simpleChineseMatch[2]);
      const endDay = parseInt(simpleChineseMatch[3]);
      
      return {
        startDate: `${currentYear}-${month.toString().padStart(2, '0')}-${startDay.toString().padStart(2, '0')}`,
        endDate: `${currentYear}-${month.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')}`,
        format: 'chinese'
      };
    }

    // 3. ISO格式和其他格式...
    const isoDateMatch = input.match(/(\d{4})-(\d{1,2})-(\d{1,2})\s*(?:to|至|到)\s*(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoDateMatch) {
      return {
        startDate: `${isoDateMatch[1]}-${isoDateMatch[2].padStart(2, '0')}-${isoDateMatch[3].padStart(2, '0')}`,
        endDate: `${isoDateMatch[4]}-${isoDateMatch[5].padStart(2, '0')}-${isoDateMatch[6].padStart(2, '0')}`,
        format: 'iso'
      };
    }

    return { startDate: null, endDate: null };
  }

  // 🆕 相对日期处理
  extractRelativeDates(input) {
    const today = new Date();
    const lowerInput = input.toLowerCase();

    // 下周
    if (lowerInput.includes('下周') || lowerInput.includes('next week')) {
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      const endDate = new Date(nextWeek);
      endDate.setDate(nextWeek.getDate() + 6); // 一周
      
      return {
        startDate: nextWeek.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    }

    // 这周末
    if (lowerInput.includes('这周末') || lowerInput.includes('this weekend')) {
      const friday = new Date(today);
      const daysUntilFriday = (5 - today.getDay() + 7) % 7;
      friday.setDate(today.getDate() + daysUntilFriday);
      
      const sunday = new Date(friday);
      sunday.setDate(friday.getDate() + 2);
      
      return {
        startDate: friday.toISOString().split('T')[0],
        endDate: sunday.toISOString().split('T')[0]
      };
    }

    return { startDate: null, endDate: null };
  }

  // 🆕 智能默认日期生成
  generateSmartDefaultDates(days, input) {
    const today = new Date();
    let startDate;

    // 根据输入内容智能选择开始时间
    if (input.includes('明天') || input.includes('tomorrow')) {
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 1);
    } else if (input.includes('下周') || input.includes('next week')) {
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 7);
    } else {
      // 默认一周后开始
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 7);
    }

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + days - 1);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }

  // 🆕 高级旅行偏好提取
  extractTravelPreferences(input) {
    const preferences = {};
    
    // 活动类型识别 - 扩展版
    const activityKeywords = {
      'adventure': ['冒险', 'adventure', '刺激', '极限', '户外运动', '探险'],
      'nature': ['自然', 'nature', '户外', '公园', '山', '海', '风景', '景色'],
      'culture': ['文化', 'culture', '博物馆', '历史', '艺术', '古迹', '传统'],
      'food': ['美食', 'food', '餐厅', '小吃', '料理', '当地菜', '特色菜'],
      'shopping': ['购物', 'shopping', '商场', '市场', '买东西', '纪念品'],
      'relaxation': ['放松', 'relax', '度假', '休闲', '温泉', 'spa', '悠闲']
    };

    // 预算类型识别 - 扩展版
    const budgetKeywords = {
      'basic': ['便宜', 'cheap', 'budget', '经济', '省钱', '实惠', '划算'],
      'signature': ['中等', 'moderate', '适中', '平衡', '中档', '标准'],
      'luxury': ['豪华', 'luxury', '高端', '奢华', '贵', '顶级', '精品']
    };

    // 季节偏好
    const seasonKeywords = {
      'spring': ['春天', 'spring', '春季'],
      'summer': ['夏天', 'summer', '夏季'],
      'fall': ['秋天', 'fall', 'autumn', '秋季'],
      'winter': ['冬天', 'winter', '冬季']
    };

    // 旅行类型
    const tripTypeKeywords = {
      'solo': ['独自', 'solo', '一个人', '单人'],
      'couple': ['情侣', 'couple', '两人', '恋人', '夫妻'],
      'family': ['家庭', 'family', '家人', '亲子', '孩子'],
      'group': ['团体', 'group', '朋友', '同事', '一群人']
    };

    // 执行匹配
    for (const [type, keywords] of Object.entries(activityKeywords)) {
      if (keywords.some(keyword => input.includes(keyword))) {
        preferences.activity = type;
        break;
      }
    }

    for (const [type, keywords] of Object.entries(budgetKeywords)) {
      if (keywords.some(keyword => input.includes(keyword))) {
        preferences.budget = type;
        break;
      }
    }

    for (const [type, keywords] of Object.entries(seasonKeywords)) {
      if (keywords.some(keyword => input.includes(keyword))) {
        preferences.season = type;
        break;
      }
    }

    for (const [type, keywords] of Object.entries(tripTypeKeywords)) {
      if (keywords.some(keyword => input.includes(keyword))) {
        preferences.tripType = type;
        break;
      }
    }

    return preferences;
  }

  // 原有方法保持兼容性
  extractDays(input) {
    return this.extractDaysAdvanced(input);
  }

  extractDateRange(input) {
    return this.extractDateRangeAdvanced(input);
  }

  extractPreferences(input) {
    return this.extractTravelPreferences(input);
  }
}